import { faMessage } from '@fortawesome/pro-solid-svg-icons';
import { FC } from 'react';

import { Body, IconBadge, IconBadgeUse, Subhead, cn } from '@growtherapy/sprout-ui';

import { CannedMessages } from '../../components/CannedMessages';
import { ChatDrawerWrapperProps as BaseChatDrawerProps } from '../../components/chat-drawer/ChatDrawer';
import { ParticipantStatus } from '../../types';
import { ProviderDrawerState } from '../drawerState';
import { CANNED_MESSAGES, NO_PARTICIPANTS_MESSAGE } from './constants';

function SessionChatPlaceholder() {
  return (
    <div className='flex flex-col gap-6 items-center h-full'>
      <IconBadge aria-hidden size='xl' icon={faMessage} use={IconBadgeUse.Lilac} className='text-[1.25rem]' />
      <section>
        <Subhead.Default as='h2' className='text-xlarge font-medium text-center mb-2 rebrand:text-text-inverse'>
          Messaging
        </Subhead.Default>
        <Body.Default className='text-neutral-700 self-center text-center rebrand:text-text-inverse'>
          Chat with clients here once they enter your waiting room.
        </Body.Default>
      </section>
    </div>
  );
}

export function ChatDrawer({
  chatDrawerComponent: BaseChatDrawer,
  drawerState,
  isEmptyState,
  isError,
  onSend,
  onQuickReplySelect = onSend,
  numberOfClientChats,
  onGoToChatList,
  isDarkTheme = false,
  ...otherProps
}: { drawerState?: ProviderDrawerState | null } & BaseChatDrawerProps & {
    numberOfClientChats: number;
    /** The drawer this provider-specific view configures. The wrapper passes the app wrapper; stories pass the pure view. */
    chatDrawerComponent: FC<BaseChatDrawerProps>;
    /** Supplied by `ChatDrawerWrapper`, which reads the theme context. */
    isDarkTheme?: boolean;
    /** Sending a canned message. The wrapper tracks it; defaults to a plain send. */
    onQuickReplySelect?: (message: string) => void;
  }) {
  const isWaitingRoomChat = drawerState === ProviderDrawerState.WAITING_ROOM_CHAT;
  const isSessionChat = drawerState === ProviderDrawerState.SESSION_CHAT;
  const isParticipantDisconnectedChat = drawerState === ProviderDrawerState.PARTICIPANT_DISCONNECTED_CHAT;
  const chatStatus = isWaitingRoomChat ? ParticipantStatus.WAITING : ParticipantStatus.IN_SESSION;

  const hasOtherAvailableChats = numberOfClientChats > 1 || (isParticipantDisconnectedChat && numberOfClientChats > 0);

  return (
    <BaseChatDrawer
      {...(hasOtherAvailableChats && { onGoToChatList })}
      {...(!isEmptyState && !isParticipantDisconnectedChat && { chatStatus })}
      headerText={
        isSessionChat && !isEmptyState ? 'Messages will persist only for the duration of the session' : undefined
      }
      emptyState={<SessionChatPlaceholder />}
      isEmptyState={isEmptyState}
      isError={isError}
      quickReplyComponent={
        isWaitingRoomChat ? (
          <CannedMessages
            messages={CANNED_MESSAGES}
            disabled={isError}
            onSelect={onQuickReplySelect}
            className={cn('px-8 pb-2 bg-neutral-400', {
              'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
            })}
          />
        ) : undefined
      }
      sendIsDisabled={isParticipantDisconnectedChat}
      onSend={onSend}
      footerText={isParticipantDisconnectedChat ? NO_PARTICIPANTS_MESSAGE : undefined}
      {...otherProps}
    />
  );
}
